import React, { useEffect, useMemo, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell,
} from "recharts";
import api from "../lib/api";
import { getJurisdictionLabel, getNetworkDisplayName } from "../lib/networkDisplay";

/**
 * Phase 3E-9L — Province/State → City drilldown chart.
 *
 * Primary bar chart aggregates fueling volume by PROVINCE (Canadian
 * networks) or STATE (USA networks). Clicking a bar drills into a
 * separate city-level chart for that jurisdiction.
 *
 * Metric is preserved through both levels (litres / gallons — chosen
 * automatically from the network's country).
 *
 * The component reuses the existing `/fueling-by-city` endpoint (which
 * already returns per-row `city, province/state, qty, amount, count`)
 * and does the province→city rollup client-side so no backend change
 * is required in this phase.
 */

// Muted industrial palette to match the rest of the app.
const BAR_PALETTE = [
  "#09090B", "#27272A", "#3F3F46", "#52525B", "#71717A", "#A1A1AA",
  "#F97316", "#FB923C", "#FACC15", "#65A30D", "#0EA5E9", "#7C3AED",
];

const _norm = (v) => (v == null ? "" : String(v)).trim();

export default function FuelingByCityChart({
  customerId,
  defaultDays = 90,
  network,   // optional filter: "esso" | "pipeline" | "petro_pass" | "ta_petro"
  country,   // optional filter: "CA" | "US"
}) {
  const [rows, setRows] = useState([]);
  const [selectedJur, setSelectedJur] = useState(null);
  const [days, setDays] = useState(defaultDays);

  useEffect(() => {
    if (!customerId) return;
    setSelectedJur(null);
    const params = { days };
    if (network) params.network = network;
    if (country) params.country = country;
    api.get(`/customers/${customerId}/fueling-by-city`, { params })
      .then(({ data }) => setRows(data.rows || []))
      .catch(() => setRows([]));
  }, [customerId, days, network, country]);

  const jurLabel = getJurisdictionLabel(network, country);
  const isUS = jurLabel === "State";
  const unit = isUS ? "GAL" : "L";
  // Phase 3E-9O — read jurisdiction from the row's own country field when
  // available. This makes the mixed-country response bucket cleanly into
  // Province vs State per row (CA rows → province, US rows → state).
  const rowJurisdiction = (r) => {
    const cc = String(r?.country || "").toUpperCase();
    if (cc === "US") return _norm(r.state);
    if (cc === "CA") return _norm(r.province);
    // Fall back to explicit panel country when the row is untagged.
    if (isUS) return _norm(r.state);
    return _norm(r.province);
  };

  // Roll up to jurisdiction level. Each row already carries city + jurisdiction.
  const jurisdictionData = useMemo(() => {
    const acc = {};
    for (const r of rows) {
      const key = rowJurisdiction(r) || "Unknown";
      const jn = key.toUpperCase();
      if (!acc[jn]) acc[jn] = { name: jn, value: 0, amount: 0, count: 0, cityCount: new Set() };
      acc[jn].value += r.qty || 0;
      acc[jn].amount += r.amount || 0;
      acc[jn].count += r.count || 0;
      if (r.city) acc[jn].cityCount.add(r.city);
    }
    return Object.values(acc)
      .map((d) => ({ ...d, cityCount: d.cityCount.size }))
      .sort((a, b) => b.value - a.value);
  }, [rows, isUS]);

  // Cities within the currently selected jurisdiction.
  const cityData = useMemo(() => {
    if (!selectedJur) return [];
    return rows
      .filter((r) => (rowJurisdiction(r) || "Unknown").toUpperCase() === selectedJur)
      .map((r) => ({
        name: _norm(r.city) || "Unknown",
        value: r.qty || 0,
        amount: r.amount || 0,
        count: r.count || 0,
      }))
      .sort((a, b) => b.value - a.value);
  }, [rows, selectedJur, isUS]);

  const chartData = selectedJur ? cityData : jurisdictionData;
  const chartTotal = chartData.reduce((s, r) => s + r.value, 0);
  const chartAmount = chartData.reduce((s, r) => s + r.amount, 0);
  const chartCount = chartData.reduce((s, r) => s + r.count, 0);

  return (
    <div className="gf-card mb-6" data-testid="fueling-jurisdiction-card">
      <div className="gf-card-header">
        <h3>
          {selectedJur
            ? <>Fueling in <span className="font-mono">{selectedJur}</span> — City Breakdown <span className="text-xs text-zinc-400">· last {days}d</span></>
            : <>Fueling by {jurLabel.toLowerCase()} <span className="text-xs text-zinc-400">· last {days}d{network ? ` · ${getNetworkDisplayName(network, country)}` : ""}</span></>}
        </h3>
        <div className="flex items-center gap-2">
          {selectedJur && (
            <button
              className="gf-btn gf-btn-ghost text-xs"
              onClick={() => setSelectedJur(null)}
              data-testid="fueling-back-to-jurisdictions"
            >← Back to {jurLabel.toLowerCase()}s</button>
          )}
          <select
            className="gf-input text-xs"
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            data-testid="fueling-window"
          >
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
            <option value={180}>180 days</option>
            <option value={365}>365 days</option>
          </select>
        </div>
      </div>
      <div className="gf-card-body">
        {chartData.length === 0 ? (
          <div className="text-sm text-zinc-400 py-10 text-center" data-testid="fueling-empty-state">
            No fuelings in this window.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div style={{ height: Math.max(340, chartData.length * 26 + 40) }}>
              <ResponsiveContainer>
                <BarChart data={chartData} layout="vertical" margin={{ left: 24, right: 24, top: 8, bottom: 8 }}>
                  <XAxis type="number" tick={{ fontSize: 10, fontFamily: "monospace" }} tickFormatter={(v) => `${v.toFixed(0)}`} />
                  <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 11, fontFamily: "monospace" }} />
                  <Tooltip
                    cursor={{ fill: "rgba(0,0,0,0.04)" }}
                    formatter={(value, _n, props) => {
                      const d = props?.payload || {};
                      const shareTxt = chartTotal ? ` (${((value / chartTotal) * 100).toFixed(1)}%)` : "";
                      return [
                        `${value.toFixed(1)} ${unit} · $${(d.amount || 0).toFixed(2)} · ${d.count || 0} fuelings${shareTxt}`,
                        selectedJur ? `${d.name}, ${selectedJur}` : d.name,
                      ];
                    }}
                  />
                  <Bar
                    dataKey="value"
                    onClick={(d) => {
                      if (!selectedJur && d && d.name && d.name !== "Unknown") {
                        setSelectedJur(d.name);
                      }
                    }}
                    cursor={!selectedJur ? "pointer" : "default"}
                  >
                    {chartData.map((_, i) => (
                      <Cell key={i} fill={BAR_PALETTE[i % BAR_PALETTE.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div>
              <table className="gf-table">
                <thead>
                  <tr>
                    <th>{selectedJur ? "CITY" : jurLabel.toUpperCase()}</th>
                    <th className="text-right">{unit === "GAL" ? "GALLONS" : "LITRES"}</th>
                    <th className="text-right">$ SPEND</th>
                    <th className="text-right">FUELINGS</th>
                    {!selectedJur && <th className="text-right">CITIES</th>}
                  </tr>
                </thead>
                <tbody>
                  {chartData.slice(0, 14).map((r, i) => {
                    const isClickable = !selectedJur && r.name && r.name !== "Unknown";
                    return (
                      <tr
                        key={i}
                        onClick={() => isClickable && setSelectedJur(r.name)}
                        style={isClickable ? { cursor: "pointer" } : undefined}
                        data-testid={selectedJur ? `fueling-city-row-${r.name}` : `fueling-jurisdiction-row-${r.name}`}
                      >
                        <td className="text-xs">{r.name}</td>
                        <td className="gf-num">{r.value.toFixed(1)}</td>
                        <td className="gf-num">${r.amount.toFixed(2)}</td>
                        <td className="gf-num">{r.count}</td>
                        {!selectedJur && <td className="gf-num">{r.cityCount}</td>}
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="overline">TOTAL</td>
                    <td className="gf-num font-semibold">{chartTotal.toFixed(1)} {unit}</td>
                    <td className="gf-num font-semibold">${chartAmount.toFixed(2)}</td>
                    <td className="gf-num font-semibold">{chartCount}</td>
                    {!selectedJur && <td/>}
                  </tr>
                </tfoot>
              </table>
              {!selectedJur && (
                <div className="text-xs text-zinc-500 mt-2 font-mono">
                  Click a bar or row to drill into cities.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
